import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  FlaggedPhrase,
  Layer1Result,
  scoreContentLayer1,
} from '../helpers/spamWords/contentScorer';
import { PatternHit } from '../helpers/spamWords/patternRules';
import {
  EditorType,
  LlmResponse,
  SpamScoreResult,
  checkSpamScore,
  computeContentHashClient,
  fetchLatestScore,
} from '../helpers/spamWords/spamScoreApi';

export interface CombinedResult {
  layer1: Layer1Result;
  layer2Score: number | null;
  finalScore: number;
  llmResponse: LlmResponse | null;
  fromCache: boolean;
  shouldHardBlock: boolean;
  checkedAt: Date | null;
  contentHashStale: boolean;
}

export interface SpamScorePanelRef {
  runLlmCheck: () => Promise<CombinedResult>;
  getCurrentResult: () => CombinedResult;
}

interface Props {
  editorType: EditorType;
  referenceId?: number | null;
  campaignId?: number | null;
  subject: string;
  preheader: string;
  body: string;
  fromName?: string;
  onResultChange?: (result: CombinedResult) => void;
  compact?: boolean;
}

const SCORE_COLORS = {
  green: '#16a34a',
  orange: '#f97316',
  red: '#dc2626',
};

function scoreColor(score: number): string {
  if (score >= 80) return SCORE_COLORS.green;
  if (score >= 50) return SCORE_COLORS.orange;
  return SCORE_COLORS.red;
}

function scoreLabel(score: number): string {
  if (score >= 80) return 'OK';
  if (score >= 50) return 'Warnung';
  return 'Kritisch';
}

function scoreEmoji(score: number): string {
  if (score >= 80) return '🟢';
  if (score >= 50) return '🟠';
  return '🔴';
}

const SEVERITY_COLORS = {
  red: '#dc2626',
  orange: '#f97316',
  yellow: '#eab308',
};

const SEVERITY_BG = {
  red: '#fef2f2',
  orange: '#fff7ed',
  yellow: '#fefce8',
};

function formatBreakdown(r: Layer1Result): string {
  const parts: string[] = [];
  if (r.breakdown.wordPenalty > 0) parts.push(`Wörter −${r.breakdown.wordPenalty}`);
  if (r.breakdown.patternPenalty > 0) parts.push(`Patterns −${r.breakdown.patternPenalty}`);
  if (r.breakdown.subjectLengthPenalty > 0)
    parts.push(`Betreff-Länge −${r.breakdown.subjectLengthPenalty}`);
  if (r.breakdown.linkDensityPenalty > 0)
    parts.push(`Link-Dichte −${r.breakdown.linkDensityPenalty}`);
  if (r.breakdown.imageOnlyPenalty > 0) parts.push(`Nur-Bilder −${r.breakdown.imageOnlyPenalty}`);
  return parts.length > 0 ? parts.join(' · ') : 'keine Abzüge';
}

/** Editoren, deren referenceId nur zusammen mit der Kampagne eindeutig ist (siehe Hydrierung unten). */
const CAMPAIGN_LOCAL_REFERENCE = new Set<string>([
  'webinar_campaign',
  'webinar_followup',
  'plx_campaign',
]);

const SpamScorePanel = forwardRef<SpamScorePanelRef, Props>((props, ref) => {
  const {
    editorType,
    referenceId,
    campaignId,
    subject,
    preheader,
    body,
    fromName,
    onResultChange,
    compact,
  } = props;

  const layer1 = useMemo<Layer1Result>(
    () => scoreContentLayer1(subject || '', preheader || '', body || ''),
    [subject, preheader, body],
  );

  const [layer2Score, setLayer2Score] = useState<number | null>(null);
  const [llmResponse, setLlmResponse] = useState<LlmResponse | null>(null);
  const [fromCache, setFromCache] = useState<boolean>(false);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);
  const [llmLoading, setLlmLoading] = useState<boolean>(false);
  const [llmError, setLlmError] = useState<string | null>(null);
  const [scoredContentHash, setScoredContentHash] = useState<string | null>(null);
  const [contentHashStale, setContentHashStale] = useState<boolean>(false);

  // Reset L2 state only when the reference (which email/campaign) changes —
  // NOT on every keystroke. Content changes are handled via staleness flag so
  // the cached verdict stays visible with a "content changed" indicator.
  React.useEffect(() => {
    setLayer2Score(null);
    setLlmResponse(null);
    setFromCache(false);
    setCheckedAt(null);
    setLlmError(null);
    setScoredContentHash(null);
    setContentHashStale(false);
  }, [editorType, referenceId]);

  // On mount / when reference changes: fetch last persisted score from DB and
  // hydrate the panel. Even if content hash differs (e.g. TinyMCE normalized
  // whitespace since the check), show the cached verdict — mark it as stale
  // so user knows to re-run the LLM check.
  React.useEffect(() => {
    if (referenceId == null) return;
    // Bei diesen Editoren ist referenceId nur INNERHALB einer Kampagne eindeutig (Mail-Nummer bzw. Position).
    // Ohne Kampagnen-Id (neuer, noch ungespeicherter Entwurf) lieferte /latest die Bewertung irgendeiner
    // fremden Kampagne mit derselben Mail-Nummer — gemessen 22.10.2026: leerer Heat-Up-Entwurf zeigte
    // „100/100 OK“ und die LLM-Analyse von „Sektor-Rotation 2026“. Ein neuer Entwurf hat nichts Gespeichertes.
    if (CAMPAIGN_LOCAL_REFERENCE.has(editorType) && campaignId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const stored = await fetchLatestScore(editorType, referenceId, campaignId ?? null);
        if (cancelled || !stored || stored.layer2_score == null) return;
        setLayer2Score(stored.layer2_score);
        setLlmResponse(stored.llm_response);
        setFromCache(true);
        const scoredAt = stored.scored_at ? new Date(stored.scored_at as unknown as string) : null;
        setCheckedAt(scoredAt);
        setScoredContentHash(stored.content_hash);
      } catch (err) {
        // Silent: hydration is best-effort
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editorType, referenceId, campaignId]);

  // Recompute stale flag whenever content or the scored hash changes.
  React.useEffect(() => {
    if (!scoredContentHash) {
      setContentHashStale(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const currentHash = await computeContentHashClient(
          subject || '',
          preheader || '',
          body || '',
        );
        if (cancelled) return;
        setContentHashStale(currentHash !== scoredContentHash);
      } catch {
        // Ignore — stale flag stays as-is
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subject, preheader, body, scoredContentHash]);

  const finalScore = useMemo(() => {
    // Stale L2 is excluded from final-score aggregation — treat as unknown.
    if (layer2Score == null || contentHashStale) return layer1.score;
    return Math.round(0.4 * layer1.score + 0.6 * layer2Score);
  }, [layer1.score, layer2Score, contentHashStale]);

  const shouldHardBlock = useMemo(() => {
    // Stale L2 doesn't count — treat as "no L2 check" for block logic so
    // re-check is required when content significantly changed.
    const effectiveL2 = contentHashStale ? null : layer2Score;
    return layer1.redCount >= 3 && (effectiveL2 == null || effectiveL2 < 30);
  }, [layer1.redCount, layer2Score, contentHashStale]);

  const currentResult: CombinedResult = useMemo(
    () => ({
      layer1,
      layer2Score,
      finalScore,
      llmResponse,
      fromCache,
      shouldHardBlock,
      checkedAt,
      contentHashStale,
    }),
    [
      layer1,
      layer2Score,
      finalScore,
      llmResponse,
      fromCache,
      shouldHardBlock,
      checkedAt,
      contentHashStale,
    ],
  );

  React.useEffect(() => {
    if (onResultChange) onResultChange(currentResult);
  }, [currentResult, onResultChange]);

  const runLlmCheck = useCallback(async (): Promise<CombinedResult> => {
    setLlmLoading(true);
    setLlmError(null);
    try {
      const result: SpamScoreResult = await checkSpamScore({
        editor_type: editorType,
        reference_id: referenceId ?? null,
        campaign_id: campaignId ?? null,
        subject,
        preheader,
        body,
        from_name: fromName,
        include_llm: true,
        force_refresh: false,
      });
      setLayer2Score(result.layer2_score);
      setLlmResponse(result.llm_response);
      setFromCache(result.from_cache);
      setCheckedAt(new Date());
      setScoredContentHash(result.content_hash);
      setContentHashStale(false);
      const combined: CombinedResult = {
        layer1,
        layer2Score: result.layer2_score,
        finalScore: result.final_score,
        llmResponse: result.llm_response,
        fromCache: result.from_cache,
        shouldHardBlock: result.should_hard_block,
        checkedAt: new Date(),
        contentHashStale: false,
      };
      return combined;
    } catch (err: any) {
      const msg = err?.message || 'LLM-Check fehlgeschlagen';
      setLlmError(msg);
      throw err;
    } finally {
      setLlmLoading(false);
    }
  }, [editorType, referenceId, campaignId, subject, preheader, body, fromName, layer1]);

  useImperativeHandle(
    ref,
    () => ({
      runLlmCheck,
      getCurrentResult: () => currentResult,
    }),
    [runLlmCheck, currentResult],
  );

  const copyReport = useCallback(() => {
    const lines: string[] = [];
    lines.push(`Spam-Score Report — ${new Date().toISOString()}`);
    lines.push(`Betreff: ${subject}`);
    lines.push(`Preheader: ${preheader}`);
    lines.push('');
    lines.push(
      `Gesamt-Score: ${finalScore}/100 (L1=${layer1.score}${layer2Score != null ? `, L2=${layer2Score}` : ''})`,
    );
    lines.push(
      `Rote Flags: ${layer1.redCount}, Orange: ${layer1.orangeCount}, Gelb: ${layer1.yellowCount}`,
    );
    if (layer1.flaggedPhrases.length > 0) {
      lines.push('');
      lines.push('Geflaggte Phrasen:');
      for (const f of layer1.flaggedPhrases) {
        lines.push(`  [${f.severity}] "${f.text}" (${f.location}) — ${f.reason}`);
      }
    }
    if (layer1.patternHits.length > 0) {
      lines.push('');
      lines.push('Pattern-Hits:');
      for (const p of layer1.patternHits) {
        lines.push(`  [${p.severity}] ${p.message}`);
      }
    }
    if (llmResponse) {
      lines.push('');
      lines.push(`LLM-Analyse: ${llmResponse.overall_assessment || '–'}`);
      if (llmResponse.rewrite_suggestions) {
        lines.push('Rewrite-Vorschläge:');
        for (const s of llmResponse.rewrite_suggestions) {
          lines.push(`  [${s.section}] "${s.current}" → "${s.suggested}"`);
        }
      }
    }
    navigator.clipboard.writeText(lines.join('\n')).catch((err) => {
      console.warn('SpamScorePanel: clipboard copy failed', err);
    });
  }, [finalScore, layer1, layer2Score, llmResponse, subject, preheader]);

  const color = scoreColor(finalScore);

  // Leerer Entwurf: kein „100/100 OK“ — ohne Inhalt gibt es nichts zu bewerten (Persona-Befund 22.10.2026).
  if (!(subject || '').trim() && !(body || '').trim()) {
    return (
      <Box
        sx={{
          border: '1px dashed #d1d5db',
          borderRadius: 2,
          p: compact ? 1.5 : 2,
          mt: 2,
          bgcolor: '#ffffff',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#111' }}>
          Content-Quality Check
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Noch kein Inhalt — die Prüfung startet, sobald Betreff oder Text da sind.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: `2px solid ${color}`,
        borderRadius: 2,
        padding: compact ? 1.5 : 2,
        backgroundColor: '#ffffff',
        marginTop: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#111' }}>
          Content-Quality Check
        </Typography>
        <Chip
          label={`${scoreEmoji(finalScore)} ${finalScore}/100 · ${scoreLabel(finalScore)}`}
          sx={{
            backgroundColor: color,
            color: '#ffffff',
            fontWeight: 'bold',
          }}
          size="small"
        />
        {fromCache && !contentHashStale && (
          <Chip
            label="aus Cache"
            size="small"
            sx={{ backgroundColor: '#e0e7ff', color: '#3730a3' }}
          />
        )}
        {contentHashStale && layer2Score != null && (
          <Chip
            label="Content seit Check geändert"
            size="small"
            sx={{
              backgroundColor: '#fef3c7',
              color: '#92400e',
              fontWeight: 'bold',
            }}
          />
        )}
        {shouldHardBlock && (
          <Chip
            label="BLOCKIEREND"
            size="small"
            sx={{
              backgroundColor: SCORE_COLORS.red,
              color: '#ffffff',
              fontWeight: 'bold',
            }}
          />
        )}
      </Stack>

      <LinearProgress
        variant="determinate"
        value={finalScore}
        sx={{
          height: 10,
          borderRadius: 5,
          backgroundColor: '#e5e7eb',
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
          },
        }}
      />

      <Stack direction="row" spacing={3} mt={1.5} flexWrap="wrap">
        <Typography variant="caption" sx={{ color: '#374151' }}>
          Layer 1 (Regex): <strong>{layer1.score}</strong>
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: contentHashStale ? '#b45309' : '#374151',
            fontStyle: contentHashStale ? 'italic' : 'normal',
          }}
        >
          Layer 2 (LLM): <strong>{layer2Score != null ? layer2Score : '–'}</strong>
          {contentHashStale && layer2Score != null ? ' (veraltet)' : ''}
        </Typography>
        <Tooltip title="Score-Abzüge im Detail">
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            {formatBreakdown(layer1)}
          </Typography>
        </Tooltip>
      </Stack>

      {llmResponse?.category_scores && (
        <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" gap={1}>
          {Object.entries(llmResponse.category_scores).map(([key, val]) => (
            <Chip
              key={key}
              label={`${key === 'urgency' ? 'Urgency' : key === 'promise' ? 'Promise' : key === 'authenticity' ? 'Authentizität' : 'Format'} ${val}`}
              size="small"
              sx={{
                backgroundColor: scoreColor(val as number),
                color: '#ffffff',
              }}
            />
          ))}
        </Stack>
      )}

      {(layer1.flaggedPhrases.length > 0 || layer1.patternHits.length > 0) && (
        <Box mt={2}>
          <Typography variant="subtitle2" sx={{ color: '#111', mb: 0.5 }}>
            Hinweise ({layer1.flaggedPhrases.length + layer1.patternHits.length})
          </Typography>
          <Stack spacing={0.5}>
            {layer1.flaggedPhrases.map((f: FlaggedPhrase, idx: number) => (
              <Box
                key={`w-${idx}`}
                sx={{
                  padding: 0.75,
                  borderRadius: 1,
                  backgroundColor: SEVERITY_BG[f.severity],
                  borderLeft: `3px solid ${SEVERITY_COLORS[f.severity]}`,
                }}
              >
                <Typography variant="body2" sx={{ color: '#111' }}>
                  <strong style={{ color: SEVERITY_COLORS[f.severity] }}>{`"${f.text}"`}</strong>{' '}
                  <span style={{ color: '#6b7280' }}>({f.location})</span> — {f.reason}
                </Typography>
                {f.suggestion && (
                  <Typography variant="caption" sx={{ color: '#4b5563' }}>
                    Tipp: {f.suggestion}
                  </Typography>
                )}
              </Box>
            ))}
            {layer1.patternHits.map((p: PatternHit, idx: number) => (
              <Box
                key={`p-${idx}`}
                sx={{
                  padding: 0.75,
                  borderRadius: 1,
                  backgroundColor: SEVERITY_BG[p.severity],
                  borderLeft: `3px solid ${SEVERITY_COLORS[p.severity]}`,
                }}
              >
                <Typography variant="body2" sx={{ color: '#111' }}>
                  {p.message} <span style={{ color: '#6b7280' }}>({p.location})</span>
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {llmResponse && (
        <Box
          mt={2}
          p={1.5}
          sx={{
            backgroundColor: contentHashStale ? '#fffbeb' : '#f0f9ff',
            borderRadius: 1,
            borderLeft: contentHashStale ? '3px solid #f59e0b' : undefined,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: contentHashStale ? '#92400e' : '#0c4a6e', mb: 0.5 }}
          >
            💡 LLM-Analyse{contentHashStale ? ' (veralteter Stand)' : ''}
          </Typography>
          {contentHashStale && (
            <Typography variant="caption" sx={{ color: '#92400e', display: 'block', mb: 1 }}>
              Der Content wurde seit dem letzten Check geändert — bitte „LLM-Check
              wiederholen&ldquo; klicken für eine aktuelle Bewertung.
            </Typography>
          )}
          {llmResponse.overall_assessment && (
            <Typography variant="body2" sx={{ color: '#111', mb: 1 }}>
              {llmResponse.overall_assessment}
            </Typography>
          )}
          {llmResponse.rewrite_suggestions && llmResponse.rewrite_suggestions.length > 0 && (
            <Stack spacing={0.5} mt={1}>
              {llmResponse.rewrite_suggestions.map((s, idx) => (
                <Box key={idx}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 'bold' }}>
                    {s.section}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#7c2d12', textDecoration: 'line-through' }}
                  >
                    {s.current}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#14532d' }}>
                    → {s.suggested}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {llmError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          LLM-Check fehlgeschlagen: {llmError} — L1-Score bleibt gültig.
        </Alert>
      )}

      <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
        <Button
          variant="outlined"
          size="small"
          startIcon={llmLoading ? <CircularProgress size={14} /> : <AutoAwesomeOutlinedIcon />}
          onClick={runLlmCheck}
          disabled={llmLoading}
          sx={{ textTransform: 'none' }}
        >
          {llmResponse ? 'LLM-Check wiederholen' : 'LLM-Check starten'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ContentCopyOutlinedIcon />}
          onClick={copyReport}
          sx={{ textTransform: 'none' }}
        >
          Report kopieren
        </Button>
        {checkedAt && (
          <Typography
            variant="caption"
            sx={{
              color: '#6b7280',
              alignSelf: 'center',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <RefreshOutlinedIcon fontSize="inherit" />
            zuletzt: {checkedAt.toLocaleTimeString()}
          </Typography>
        )}
      </Stack>
    </Box>
  );
});

SpamScorePanel.displayName = 'SpamScorePanel';

export default SpamScorePanel;
